import { useState } from 'react'
import type { GalleryImage } from '../types/config'
import { PreviewModal } from './PreviewModal'

interface GalleryProps {
  images: GalleryImage[]
  loading: boolean
  title?: string
  downloadText?: string
  closeText?: string
}

export function Gallery({ 
  images, 
  loading, 
  title = 'Gallery',
  downloadText,
  closeText
}: GalleryProps) {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null)

  if (loading) {
    return (
      <section className="py-8">
        <h2 className="text-xl font-semibold text-exness-dark mb-6">{title}</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
          {[...Array(8)].map((_, i) => (
            <div 
              key={i}
              className="aspect-square bg-gray-100 rounded-xl animate-pulse-slow"
            />
          ))}
        </div>
      </section>
    )
  }

  if (images.length === 0) {
    return (
      <section className="py-8">
        <h2 className="text-xl font-semibold text-exness-dark mb-6">{title}</h2>
        <div className="text-center py-12 text-gray-500">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>No icons generated yet</p>
        </div>
      </section>
    )
  }

  return (
    <section className="py-8">
      <h2 className="text-xl font-semibold text-exness-dark mb-6">{title}</h2>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
        {images.map((image, index) => (
          <button
            key={image.name}
            onClick={() => setSelectedImage(image)}
            className="aspect-square rounded-xl overflow-hidden bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHJlY3QgZmlsbD0iI2YzZjRmNiIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIi8+PHJlY3QgZmlsbD0iI2ZmZiIgeD0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIvPjxyZWN0IGZpbGw9IiNmZmYiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiLz48cmVjdCBmaWxsPSIjZjNmNGY2IiB4PSIxMCIgeT0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNncmlkKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] border border-gray-200 hover:border-exness-yellow hover:shadow-lg transition-all duration-200 group"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <img 
              src={image.url} 
              alt="Generated icon"
              className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-200"
              loading="lazy"
            />
          </button>
        ))}
      </div>

      <PreviewModal
        imageUrl={selectedImage?.url || ''}
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        downloadText={downloadText}
        closeText={closeText}
      />
    </section>
  )
}
